import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import Modal from "../Modal";

const isFileValue = (value) => {
	if (value instanceof File) return value.size > 0;
	if (typeof FileList !== "undefined" && value instanceof FileList) {
		return value.length > 0;
	}
	if (Array.isArray(value)) {
		return value.length > 0 && value.every((item) => item instanceof File);
	}
	return false;
};

/** Existing ACF/WP image (edit mode) — not a fresh File upload */
const isExistingImageValue = (value) => {
	if (!value) return false;
	if (typeof value === "string") {
		return (
			value.startsWith("http") ||
			value.startsWith("/") ||
			/^\d+$/.test(value)
		);
	}
	if (typeof value === "number" && value > 0) return true;
	if (typeof value === "object") {
		if (value.url || value.ID || value.id) return true;
		if (Array.isArray(value) && value.some((item) => isExistingImageValue(item))) {
			return true;
		}
	}
	return false;
};

const getImageUrlFromValue = (value) => {
	if (!value) return null;
	if (typeof value === "string" && (value.startsWith("http") || value.startsWith("/"))) {
		return value;
	}
	if (typeof value === "object" && value.url) return value.url;
	return null;
};

// Helper function to create cropped image
const createCroppedImage = async (imageSrc, croppedAreaPixels) => {
	const image = new Image();
	image.src = imageSrc;

	return new Promise((resolve, reject) => {
		image.onload = () => {
			try {
				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d");

				canvas.width = croppedAreaPixels.width;
				canvas.height = croppedAreaPixels.height;

				ctx.drawImage(
					image,
					croppedAreaPixels.x,
					croppedAreaPixels.y,
					croppedAreaPixels.width,
					croppedAreaPixels.height,
					0,
					0,
					croppedAreaPixels.width,
					croppedAreaPixels.height
				);

				canvas.toBlob(
					(blob) => {
						if (!blob) {
							reject(new Error("Failed to create cropped image"));
							return;
						}
						resolve(blob);
					},
					"image/jpeg",
					0.9
				);
			} catch (error) {
				reject(error);
			}
		};
		image.onerror = () =>
			reject(new Error("Failed to load image for cropping"));
	});
};

// Simple cropper modal component
const CropperModal = ({ imageSrc, onComplete, onCancel, aspectRatio = 1 }) => {
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

	const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	}, []);

	const handleSave = async () => {
		if (!croppedAreaPixels) return;
		try {
			const croppedBlob = await createCroppedImage(
				imageSrc,
				croppedAreaPixels
			);
			onComplete(croppedBlob);
		} catch (error) {
			console.error("Crop failed:", error);
			alert(
				"We couldn't crop that image. Please try a different photo or a smaller file."
			);
		}
	};

	return (
		<div className="cropper-content">
			<div className="cropper-header">
				<h3>Crop Image</h3>
				<div className="cropper-actions">
					<button type="button" onClick={onCancel} className="btn-cancel">
						Cancel
					</button>
					<button type="button" onClick={handleSave} className="btn-save">
						Save
					</button>
				</div>
			</div>

			<div className="cropper-container">
				<div style={{ position: "relative", width: "100%", height: "100%" }}>
					<Cropper
						image={imageSrc}
						crop={crop}
						zoom={zoom}
						aspect={aspectRatio}
						onCropChange={setCrop}
						onZoomChange={setZoom}
						onCropComplete={onCropComplete}
						showGrid={true}
						cropShape="rect"
					/>
				</div>
			</div>

			<div className="cropper-controls">
				<label>
					Zoom:
					<input
						type="range"
						min={1}
						max={3}
						step={0.1}
						value={zoom}
						onChange={(e) => setZoom(e.target.value)}
					/>
				</label>
			</div>
		</div>
	);
};

const getDropRejectionMessage = (rejectedFiles, maxSize, maxFiles) => {
	if (!rejectedFiles?.length) return null;

	const rejection = rejectedFiles[0];
	const code = rejection?.errors?.[0]?.code;

	switch (code) {
		case "file-too-large":
			return `File is too large. Maximum size is ${maxSize / 1024 / 1024}MB.`;
		case "file-invalid-type":
			return "Invalid file type. Please upload JPG, PNG, GIF, or WebP.";
		case "too-many-files":
			return `You can only upload ${maxFiles} file${maxFiles === 1 ? "" : "s"}.`;
		default:
			return (
				rejection?.errors?.[0]?.message ||
				"That file could not be uploaded."
			);
	}
};

const ImageUpload = ({
	register,
	unregister,
	error,
	required = false,
	className = "",
	id,
	setValue,
	multi = true,
	watch,
	crop = false,
	maxFiles = 5,
	maxSize = 5242880, // 5MB
	acceptedFileTypes = {
		"image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
	},
	placeholder = "Drag & drop images here, or click to select files",
	cropAspectRatio = 1, // Default to square crop
}) => {
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [cropperState, setCropperState] = useState({
		isOpen: false,
		imageSrc: null,
		originalFile: null,
		fileIndex: null,
	});
	const [hasHydrated, setHasHydrated] = useState(false);
	const [dropError, setDropError] = useState(null);
	const displayError = error?.message || dropError;

	const setFileValue = (value) => {
		// Keep Files in RHF state only — never bind them to a DOM input (coerces to "[object File]")
		setValue(id, value, { shouldValidate: true, shouldDirty: true });
	};

	// Register without a hidden <input> so File values are not stringified by the browser
	useEffect(() => {
		register(id, {
			required: required
				? `Please upload ${multi ? "at least one image" : "an image"}`
				: false,
			validate: (value) => {
				if (!required) return true;
				if (isFileValue(value)) return true;
				// Existing ACF image / URL / attachment ID (edit mode)
				if (isExistingImageValue(value)) return true;
				return `Please upload ${
					multi ? "at least one image" : "an image"
				}`;
			},
		});
		return () => {
			unregister?.(id);
		};
	}, [register, unregister, id, required, multi]);

	//useEffect to hydrate uploadedFiles from form value if present - only on initial load
	useEffect(() => {
		// Only hydrate on initial load, not when files are uploaded
		if (hasHydrated) return;

		const urlToFile = async (
			url,
			filename = "image.jpg",
			mimeType = "image/jpeg"
		) => {
			const response = await fetch(url);
			const blob = await response.blob();
			return new File([blob], filename, { type: mimeType });
		};

		const hydrateFiles = async (formValue) => {
			if (!formValue) {
				setUploadedFiles([]);
				setHasHydrated(true);
				return;
			}
			if (formValue instanceof File) {
				setUploadedFiles([
					{
						file: formValue,
						id: Math.random().toString(36).substr(2, 9),
						preview: URL.createObjectURL(formValue),
					},
				]);
			} else if (Array.isArray(formValue)) {
				// Array of Files, ACF gallery rows, or URL objects
				const hydrated = await Promise.all(
					formValue.map(async (item) => {
						if (item instanceof File) {
							return {
								file: item,
								id: Math.random().toString(36).substr(2, 9),
								preview: URL.createObjectURL(item),
							};
						}
						const url =
							item?.url ||
							(typeof item === "string" && item.startsWith("http")
								? item
								: null);
						if (!url) return null;
						const filename =
							item?.filename || item?.name || "image.jpg";
						const mimeType =
							item?.mime_type || item?.type || "image/jpeg";
						try {
							const file = await urlToFile(url, filename, mimeType);
							return {
								file,
								id: Math.random().toString(36).substr(2, 9),
								preview: URL.createObjectURL(file),
							};
						} catch (e) {
							console.warn("Could not hydrate image:", e);
							return {
								file: { name: filename },
								id: Math.random().toString(36).substr(2, 9),
								preview: url,
							};
						}
					})
				);
				setUploadedFiles(hydrated.filter(Boolean));
			} else if (getImageUrlFromValue(formValue)) {
				const url = getImageUrlFromValue(formValue);
				const filename =
					formValue.filename || formValue.name || "image.jpg";
				const mimeType =
					formValue.mime_type || formValue.type || "image/jpeg";
				try {
					const file = await urlToFile(url, filename, mimeType);
					setUploadedFiles([
						{
							file,
							id: Math.random().toString(36).substr(2, 9),
							preview: URL.createObjectURL(file),
						},
					]);
				} catch (e) {
					console.warn("Could not hydrate image from URL:", e);
					// Still show preview so edit mode isn't blank
					setUploadedFiles([
						{
							file: { name: filename },
							id: Math.random().toString(36).substr(2, 9),
							preview: url,
						},
					]);
				}
			} else {
				setUploadedFiles([]);
			}
			setHasHydrated(true);
		};
		hydrateFiles(watch(id));
	}, [watch(id), id, hasHydrated]);

	const openCropper = (file, index = null) => {
		const imageSrc = URL.createObjectURL(file);
		setCropperState({
			isOpen: true,
			imageSrc,
			originalFile: file,
			fileIndex: index,
		});
	};

	const handleCropComplete = (croppedBlob) => {
		if (!croppedBlob) {
			console.error("Crop produced an empty image");
			alert(
				"We couldn't crop that image. Please try a different photo or a smaller file."
			);
			closeCropper();
			return;
		}

		// Get the original filename
		const originalName = cropperState.originalFile.name;

		// The cropped blob is always JPEG (as defined in createCroppedImage)
		const croppedMimeType = "image/jpeg";

		// Extract base name without extension
		const baseName = originalName.replace(/\.[^/.]+$/, "");

		// Create filename with correct extension for JPEG output
		const newFileName = `${baseName}_cropped.jpg`;

		const croppedFile = new File([croppedBlob], newFileName, {
			type: croppedMimeType,
		});

		if (multi) {
			if (cropperState.fileIndex !== null) {
				// Update existing file
				setUploadedFiles((prev) => {
					const updated = [...prev];
					URL.revokeObjectURL(updated[cropperState.fileIndex].preview);
					updated[cropperState.fileIndex] = {
						...updated[cropperState.fileIndex],
						file: croppedFile,
						preview: URL.createObjectURL(croppedFile),
					};
					setFileValue(updated.map((f) => f.file));
					return updated;
				});
			} else {
				// Add new file
				const newFile = {
					file: croppedFile,
					id: Math.random().toString(36).substr(2, 9),
					preview: URL.createObjectURL(croppedFile),
				};
				setUploadedFiles((prev) => {
					const updated = [...prev, newFile];
					setFileValue(updated.map((f) => f.file));
					return updated;
				});
			}
		} else {
			// Single file mode
			const newFile = {
				file: croppedFile,
				id: Math.random().toString(36).substr(2, 9),
				preview: URL.createObjectURL(croppedFile),
			};
			setUploadedFiles([newFile]);
			setFileValue(croppedFile);
		}

		closeCropper();
	};

	const closeCropper = () => {
		if (cropperState.imageSrc) {
			URL.revokeObjectURL(cropperState.imageSrc);
		}
		setCropperState({
			isOpen: false,
			imageSrc: null,
			originalFile: null,
			fileIndex: null,
		});
	};

	const onDrop = useCallback(
		(acceptedFiles, rejectedFiles) => {
			if (rejectedFiles.length > 0) {
				setDropError(
					getDropRejectionMessage(
						rejectedFiles,
						maxSize,
						multi ? maxFiles : 1
					)
				);
			}

			if (acceptedFiles.length === 0) return;

			setDropError(null);

			const file = acceptedFiles[0];

			if (crop) {
				openCropper(file);
			} else {
				processFiles(acceptedFiles);
			}
		},
		[crop, multi, maxFiles, maxSize, uploadedFiles.length]
	);

	const processFiles = (files) => {
		if (multi) {
			const newFiles = files
				.slice(0, maxFiles - uploadedFiles.length)
				.map((file) => ({
					file,
					id: Math.random().toString(36).substr(2, 9),
					preview: URL.createObjectURL(file),
				}));

			setUploadedFiles((prev) => {
				const updated = [...prev, ...newFiles];
				setFileValue(updated.map((f) => f.file));
				return updated;
			});
		} else {
			const newFile = {
				file: files[0],
				id: Math.random().toString(36).substr(2, 9),
				preview: URL.createObjectURL(files[0]),
			};
			setUploadedFiles([newFile]);
			setFileValue(files[0]);
		}
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: acceptedFileTypes,
		maxFiles: multi ? maxFiles : 1,
		maxSize,
		multiple: multi,
	});

	const removeFile = (fileId) => {
		setUploadedFiles((prev) => {
			const fileToRemove = prev.find((f) => f.id === fileId);
			if (fileToRemove) {
				URL.revokeObjectURL(fileToRemove.preview);
			}
			const updated = prev.filter((f) => f.id !== fileId);
			setFileValue(multi ? updated.map((f) => f.file) : null);
			return updated;
		});
	};

	const removeAllFiles = () => {
		uploadedFiles.forEach((file) => URL.revokeObjectURL(file.preview));
		setUploadedFiles([]);
		setFileValue(multi ? [] : null);
	};

	return (
		<div className={`image-upload ${className}`}>
			{/* Dropzone area - only show if no files uploaded or if multi and under max */}
			{(!multi && uploadedFiles.length === 0) ||
			(multi && uploadedFiles.length < maxFiles) ? (
				<div
					{...getRootProps()}
					className={`dropzone ${isDragActive ? "dropzone--active" : ""} ${
						displayError ? "dropzone--error" : ""
					}`}
				>
					<input {...getInputProps()} />
					<div className="dropzone-content">
						<div className="dropzone-icon">📸</div>
						<p className="dropzone-text">
							{isDragActive ? "Drop the images here..." : placeholder}
						</p>
						<p className="dropzone-hint">
							Supports: JPG, PNG, GIF, WebP
							{multi && ` (max ${maxFiles} files, `}
							{!multi && " (max "}
							{maxSize / 1024 / 1024}MB{multi ? " each)" : ")"}
							{crop && " • Images will be cropped after upload"}
						</p>
					</div>
				</div>
			) : null}

			{/* File previews */}
			{uploadedFiles.length > 0 && (
				<div className="previews">
					<div className="previews-header">
						<h4>
							{multi
								? `Uploaded Images (${uploadedFiles.length}/${maxFiles})`
								: "Uploaded Image"}
						</h4>
						<div className="previews-actions">
							{/* Show "Add More" button for multi upload when under max */}
							{multi && uploadedFiles.length < maxFiles && (
								<button
									type="button"
									className="btn-add-more"
									onClick={() => document.querySelector(".dropzone")?.click()}
								>
									Add More
								</button>
							)}
							{uploadedFiles.length > 1 && (
								<button
									type="button"
									className="btn-remove-all"
									onClick={removeAllFiles}
								>
									Remove All
								</button>
							)}
						</div>
					</div>

					<div
						className={`preview-grid ${!multi ? "preview-grid--single" : ""}`}
					>
						{uploadedFiles.map((fileData, index) => (
							<div key={fileData.id} className="preview-item">
								<div className="preview-image">
									<img src={fileData.preview} alt={fileData.file.name} />

									{crop && (
										<button
											type="button"
											className="btn-crop"
											onClick={() => openCropper(fileData.file, index)}
											title="Crop image"
										>
											✂️
										</button>
									)}

									<button
										type="button"
										className="btn-remove"
										onClick={() => removeFile(fileData.id)}
										title="Remove image"
									>
										✕
									</button>
								</div>

								<div className="preview-info">
									<p className="file-name">
										{fileData.file?.name || "Image"}
									</p>
									{typeof fileData.file?.size === "number" && (
										<p className="file-size">
											{(fileData.file.size / 1024 / 1024).toFixed(2)} MB
										</p>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{displayError && <div className="field__error">{displayError}</div>}

			{/* Cropper Modal using Modal component */}
			<Modal
				isOpen={cropperState.isOpen}
				onClose={closeCropper}
				title="Crop Image"
				allowClose={true}
			>
				{cropperState.isOpen && (
					<CropperModal
						imageSrc={cropperState.imageSrc}
						onComplete={handleCropComplete}
						onCancel={closeCropper}
						aspectRatio={cropAspectRatio}
					/>
				)}
			</Modal>

			<style jsx>{`
				.image-upload {
					width: 100%;
				}

				.dropzone {
					border: 2px dashed #ddd;
					border-radius: 8px;
					padding: 40px 20px;
					text-align: center;
					cursor: pointer;
					transition: all 0.2s ease;
				}

				.dropzone:hover,
				.dropzone--active {
					border-color: #007cba;
					background-color: #f8f9fa;
				}

				.dropzone--error {
					border-color: #dc3545;
				}

				.dropzone-content {
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: 12px;
				}

				.dropzone-icon {
					font-size: 48px;
					opacity: 0.5;
				}

				.dropzone-text {
					font-size: 16px;
					font-weight: 500;
					margin: 0;
				}

				.dropzone-hint {
					font-size: 14px;
					color: #6c757d;
					margin: 0;
				}

				.previews {
					margin-top: 20px;
				}

				.previews-header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 16px;
				}

				.previews-header h4 {
					margin: 0;
					font-size: 16px;
				}

				.previews-actions {
					display: flex;
					gap: 12px;
				}

				.btn-add-more {
					padding: 6px 12px;
					background: #007cba;
					color: white;
					border: none;
					border-radius: 4px;
					cursor: pointer;
					font-size: 14px;
				}

				.btn-remove-all {
					padding: 6px 12px;
					background: #dc3545;
					color: white;
					border: none;
					border-radius: 4px;
					cursor: pointer;
					font-size: 14px;
				}

				.preview-grid {
					display: grid;
					grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
					gap: 16px;
				}

				.preview-grid--single {
					max-width: 250px;
					margin: 0 auto;
				}

				.preview-item {
					border: 1px solid #e9ecef;
					border-radius: 8px;
					overflow: hidden;
					background: white;
				}

				.preview-image {
					position: relative;
					aspect-ratio: 1;
					overflow: hidden;
				}

				.preview-image img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}

				.btn-crop {
					position: absolute;
					top: 8px;
					right: 40px;
					background: rgba(0, 0, 0, 0.7);
					color: white;
					border: none;
					border-radius: 4px;
					padding: 6px;
					cursor: pointer;
					opacity: 0;
					transition: opacity 0.2s;
				}

				.btn-remove {
					position: absolute;
					top: 8px;
					right: 8px;
					background: rgba(220, 53, 69, 0.8);
					color: white;
					border: none;
					border-radius: 50%;
					width: 24px;
					height: 24px;
					cursor: pointer;
					display: flex;
					align-items: center;
					justify-content: center;
					opacity: 0;
					transition: opacity 0.2s;
				}

				.preview-item:hover .btn-crop,
				.preview-item:hover .btn-remove {
					opacity: 1;
				}

				.preview-info {
					padding: 12px;
				}

				.file-name {
					font-size: 14px;
					font-weight: 500;
					margin: 0 0 4px 0;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
				}

				.file-size {
					font-size: 12px;
					color: #6c757d;
					margin: 0;
				}

				.cropper-content {
					background: white;
					border-radius: 8px;
					width: 90vw;
					max-width: 600px;
					max-height: 90vh;
					display: flex;
					flex-direction: column;
				}

				.cropper-header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					padding: 20px;
					border-bottom: 1px solid #e9ecef;
				}

				.cropper-header h3 {
					margin: 0;
				}

				.cropper-actions {
					display: flex;
					gap: 12px;
				}

				.btn-cancel {
					padding: 8px 16px;
					border: 1px solid #ddd;
					background: white;
					border-radius: 4px;
					cursor: pointer;
				}

				.btn-save {
					padding: 8px 16px;
					background: #007cba;
					color: white;
					border: none;
					border-radius: 4px;
					cursor: pointer;
				}

				.cropper-container {
					position: relative;
					width: 100%;
					height: 400px;
					background: #f8f9fa;
				}

				.cropper-controls {
					padding: 20px;
					border-top: 1px solid #e9ecef;
				}

				.cropper-controls label {
					display: flex;
					align-items: center;
					gap: 12px;
					font-size: 14px;
				}

				.cropper-controls input[type="range"] {
					flex: 1;
				}
			`}</style>
		</div>
	);
};

export default ImageUpload;
